import React from "react";
import Card from "../canon/Card";
import { Wrench as WrenchIcon } from "lucide-react";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * ToolsGridTemplate
 * - Sidebar/panel grid showing a curated set of tools with icons and labels.
 * - Consistent header, spacing, and interactive states for tool shortcuts.
 */
export function ToolsGridTemplate({
  title = "Popular Tools",
  subtitle = "Quick access to your tools",
  showHeaderIcon = false, // compact by default for sidebar usage
  icon: Icon = WrenchIcon,
  iconBgClass = "bg-succession-primary/10",
  iconColorClass = "text-succession-primary",
  tools = [],
  cardVariant = "borderless", // or 'subtle'
  className = "",
  gridClassName = "",
  renderItem, // optional custom renderer: (tool) => ReactNode
}) {
  return (
    <Card variant={cardVariant} className={cx("overflow-hidden p-0", className)}>
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        {showHeaderIcon ? (
          // Full header with icon - for standalone cards
          <div className="flex items-start gap-4">
            <div className={cx("w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0", iconBgClass)}>
              <Icon className={cx("w-6 h-6", iconColorClass)} />
            </div>
            <div>
              <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{title}</h3>
              <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>{subtitle}</p>
            </div>
          </div>
        ) : (
          // Compact header - for sidebar usage
          <div>
            <h3 className="font-semibold text-lg text-succession-text-primary leading-tight" style={{ margin: 0, padding: 0 }}>{title}</h3>
            <p className="text-sm text-succession-text-secondary leading-tight" style={{ margin: 0, padding: 0 }}>{subtitle}</p>
          </div>
        )}
      </div>

      {/* Tools Grid */}
      <div className="px-6 pb-6">
        <div className={cx("grid grid-cols-2 gap-4", gridClassName)}>
          {tools.map((tool) => (
            renderItem ? (
              <React.Fragment key={tool.id || tool.label}>{renderItem(tool)}</React.Fragment>
            ) : (
              <a
                key={tool.id || tool.label}
                href={tool.route || tool.href || "#"}
                className="flex flex-col items-center gap-2 p-2 rounded-md hover:bg-succession-hover-bg focus-visible:ring-2 focus-visible:ring-succession-focus-ring text-succession-text-secondary hover:text-succession-text-primary text-center"
              >
                <div className="w-12 h-12 rounded-2xl bg-succession-secondary flex items-center justify-center text-white">
                  {tool.icon ? (
                    <tool.icon className="w-5 h-5 md:w-6 md:h-6" />
                  ) : (
                    <span className="w-5 h-5" />
                  )}
                </div>
                <span className="font-medium leading-snug text-succession-text-primary text-center whitespace-normal break-words text-xs">
                  {tool.label}
                </span>
                {tool.comingSoon && (
                  <span className="text-xs text-succession-text-secondary">Soon</span>
                )}
              </a>
            )
          ))}
        </div>
      </div>
    </Card>
  );
}
